import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Download, CheckCircle2, Star, FileText, Users, Heart, TrendingUp, Shield, School, Briefcase } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DigitalResources = () => {
  const [loadingEbook, setLoadingEbook] = useState(null); // Track which e-book is loading

  const ebooks = [
    {
      id: 'anxiety-guide',
      title: 'Managing Anxiety: A Practical 21-Day Guide',
      description: '21-day structured program with daily exercises, worksheets, breathing techniques, and thought journals to help you manage anxiety effectively.',
      price: 499,
      pages: '40-50',
      icon: Shield,
      features: [
        '21-day structured program',
        '35+ fillable worksheets',
        'Daily exercises & techniques',
        'Thought journaling templates',
        'Progress trackers'
      ],
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'stress-management',
      title: 'Stress Management for Working Professionals',
      description: 'Comprehensive guide for managing workplace stress, including time management tools, work-life balance strategies, and burnout prevention.',
      price: 699,
      pages: '50-60',
      icon: Briefcase,
      features: [
        'Workplace stress assessment',
        'Time management tools',
        'Work-life balance strategies',
        '40+ fillable worksheets',
        '90-day action plan'
      ],
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 'self-esteem',
      title: 'Building Self-Esteem: Your Journey to Confidence',
      description: 'Build lasting self-confidence with self-worth exercises, confidence-building activities, and negative self-talk reframing techniques.',
      price: 599,
      pages: '45-55',
      icon: Heart,
      features: [
        'Self-esteem assessment tools',
        'Confidence-building exercises',
        'Daily affirmations creator',
        '30-day challenge',
        'Progress trackers'
      ],
      color: 'from-pink-500 to-pink-600'
    },
    {
      id: 'relationship-guide',
      title: 'Navigating Relationship Challenges',
      description: 'Master communication frameworks, conflict resolution strategies, and healthy boundaries to strengthen your relationships.',
      price: 799,
      pages: '60-70',
      icon: Users,
      features: [
        'Communication frameworks',
        'Conflict resolution strategies',
        'Attachment styles guide',
        '50+ conversation starters',
        'Relationship growth plan'
      ],
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'emotional-resilience',
      title: 'Emotional Resilience: Bouncing Back from Challenges',
      description: 'Develop resilience with coping strategies toolkit, growth mindset exercises, and crisis management techniques.',
      price: 699,
      pages: '50-60',
      icon: TrendingUp,
      features: [
        'Resilience assessment',
        'Coping strategies toolkit',
        'Growth mindset exercises',
        'Crisis management guide',
        '90-day resilience plan'
      ],
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'adolescent-psychology',
      title: 'Supporting Your Teen: A Parent\'s Guide',
      description: 'Comprehensive guide to adolescent psychology, covering teenage brain development, common challenges, communication strategies, and warning signs.',
      price: 899,
      pages: '70-80',
      icon: School,
      features: [
        'Teenage brain development',
        'Communication strategies',
        'Warning signs guide',
        'Crisis resources',
        '55+ fillable worksheets'
      ],
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  const handlePurchase = async (ebook) => {
    setLoadingEbook(ebook.id);
    try {
      // Create Razorpay order
      const { data } = await axios.post(`${API}/create-ebook-order`, {
        ebook_id: ebook.id,
        ebook_title: ebook.title,
        amount: ebook.price,
        customer_email: '' // Will be collected in Razorpay form
      });

      if (data.error) {
        toast.error(data.error);
        setLoadingEbook(null);
        return;
      }

      const options = {
        key: data.razorpay_key_id,
        amount: ebook.price * 100,
        currency: 'INR',
        name: 'MIND FRAME',
        description: ebook.title,
        order_id: data.razorpay_order_id,
        handler: async function (response) {
          try {
            // Verify payment and deliver ebook
            const verifyResponse = await axios.post(`${API}/verify-ebook-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              ebook_id: ebook.id
            });

            if (verifyResponse.data.status === 'success') {
              toast.success('Payment successful! Check your email for download link.');
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
            setLoadingEbook(null);
          } catch (error) {
            toast.error('Verification failed. Please contact support with your payment ID.');
            setLoadingEbook(null);
          }
        },
        modal: {
          ondismiss: function() {
            setLoadingEbook(null);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setLoadingEbook(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">MIND FRAME</h1>
              <p className="text-sm text-slate-600">Digital Resources</p>
            </div>
          </div>
          <Button onClick={() => window.location.href = '/'} variant="outline">
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-slate-800 mb-6">
            Self-Help E-Books & Workbooks
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Professional mental health resources you can access instantly. Interactive PDFs with fillable worksheets designed to support your emotional well-being.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Instant Download</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Interactive PDFs</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Fillable Worksheets</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Lifetime Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* E-Books Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ebooks.map((ebook) => {
              const Icon = ebook.icon;
              return (
                <Card key={ebook.id} className="border-none shadow-xl hover:shadow-2xl transition-shadow">
                  <CardHeader>
                    <div className={`w-16 h-16 bg-gradient-to-br ${ebook.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl mb-2">{ebook.title}</CardTitle>
                    <CardDescription className="text-slate-600">
                      {ebook.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {ebook.pages} pages
                        </span>
                        <span className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          PDF Format
                        </span>
                      </div>
                      
                      <div className="border-t pt-4">
                        <p className="text-sm font-semibold text-slate-700 mb-2">What's Included:</p>
                        <ul className="space-y-1 text-sm text-slate-600">
                          {ebook.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="border-t pt-4 flex items-center justify-between">
                        <div>
                          <p className="text-3xl font-bold text-slate-800">₹{ebook.price}</p>
                          <p className="text-xs text-slate-500">One-time payment</p>
                        </div>
                        <Button 
                          onClick={() => handlePurchase(ebook)}
                          disabled={loadingEbook === ebook.id}
                          className={`bg-gradient-to-r ${ebook.color} text-white hover:opacity-90`}
                        >
                          {loadingEbook === ebook.id ? 'Processing...' : 'Buy Now'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-slate-800 mb-8">Frequently Asked Questions</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">How do I receive my e-book?</h4>
              <p className="text-slate-600">After successful payment, you'll receive an email with a download link instantly. The e-book is yours to keep forever.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Are these interactive PDFs?</h4>
              <p className="text-slate-600">Yes! All e-books include fillable worksheets, checkboxes, and text fields that you can complete digitally.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Can I get a refund?</h4>
              <p className="text-slate-600">Due to the digital nature of the product, we offer refunds within 7 days if you're unsatisfied. Contact us at hello@mfcounsellingservices.com.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Is this a replacement for therapy?</h4>
              <p className="text-slate-600">No. These e-books are self-help resources to complement professional support, not replace it. If you're in crisis, please contact emergency services or book a session.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400">© 2024 MIND FRAME. All rights reserved.</p>
          <p className="text-sm text-slate-500 mt-2">Secure payments powered by Razorpay</p>
        </div>
      </footer>
    </div>
  );
};

export default DigitalResources;
